import { useUserOrganization } from '@client/store/organization';
import { usePaywallStore } from './paywall.store';
import { useMemo } from 'react';
import amplitudeLog from '@client/amplitude';
import {
  PaywallFeature,
  proFeatures,
  freePlanFeatures,
  projectIncreasingFeatures,
  PlanData,
} from '@shared/models/paywall.model';
import { AMP_PAYWALL_OPEN } from '@shared/constants';
import {
  isOverMaxProjectCount,
  getPaywallPlan,
  isActiveProject,
  parseMaxProjectCount,
} from '@shared/helpers/paywall.helpers';
import { useProjects } from '@client/store/project';
import { useSharedMemo } from '@client/hooks/use-utils.hooks';

export const usePaywallPlan = (): PlanData['plan'] => {
  const maxProjectCount = useMaxProjectCount();
  return useSharedMemo(
    () => getPaywallPlan(maxProjectCount),
    'usePaywallPlan',
    [maxProjectCount],
  );
};

/**
 * Get the maximum number of projects allowed for the organization
 * @returns Maximum number of projects allowed
 */
export const useMaxProjectCount = (): number | undefined => {
  const organization = useUserOrganization();
  return useMemo(
    () => parseMaxProjectCount(organization?.metadata?.max_project_count),
    [organization],
  );
};

/**
 * Get the number of active projects
 * @returns Number of active projects
 */
const useActiveProjectCount = (): number => {
  const projects = useProjects();
  return useSharedMemo(
    () => projects.filter(isActiveProject).length, // Expensive operation, shared memoize it
    'useActiveProjectCount',
    [projects],
  );
};

/**
 * Check if the number of active projects has exceeded the allowed limit
 * @returns True if number of active projects is over the allowed limit
 */
export const useIsOverMaxProjectCountLimit = (): boolean => {
  const maxProjectCount = useMaxProjectCount();
  const activeProjectCount = useActiveProjectCount();
  return useMemo(() => {
    return isOverMaxProjectCount(activeProjectCount, maxProjectCount);
  }, [activeProjectCount, maxProjectCount]);
};

export const useOpenPaywallDialog = () => {
  const openDialog = usePaywallStore((state) => state.openDialog);
  const derived = usePaywallStore((state) => state.derived);

  // When sync has not run yet (e.g. before Paywall mounts), allow all to avoid blocking
  const plan = derived?.plan ?? 'enterprise';
  const isOverLimit = derived?.overLimit ?? false;

  return useMemo(
    () => ({
      openPaywallDialog: (action: PaywallFeature) => {
        openDialog();
        amplitudeLog(AMP_PAYWALL_OPEN, { action });
      },
      /**
       * True if the action is blocked: not in the plan's allowed set, or at project limit
       * and action is in {@link projectIncreasingFeatures}. Enterprise has no limit.
       *
       * @param action - The feature action to check
       * @returns true if the action is blocked by the paywall
       */
      isBehindPaywall: (action: PaywallFeature) => {
        switch (plan) {
          case 'free': {
            if (!freePlanFeatures.includes(action)) return true; // not allowed
            return projectIncreasingFeatures.includes(action)
              ? isOverLimit
              : false;
          }
          case 'pro': {
            if (!proFeatures.includes(action)) return true;
            return projectIncreasingFeatures.includes(action)
              ? isOverLimit
              : false;
          }
          case 'enterprise':
            return isOverLimit;
        }
      },
    }),
    [openDialog, plan, isOverLimit],
  );
};
